/*
 * Hero Section – Full viewport with watercolor background
 * Sharon's photo on the right (RTL), headline + CTA on the left
 * Botanical Wellness style with organic shapes
 */

import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_BG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/hero-bg-hu6ZEkpskkzKPwWoa7GpiG.webp";
const SHARON_PILATES = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/sharon-pilates_13edda1a.jpeg";

export default function HeroSection() {
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Watercolor background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="מאמנת כושר בשוהם - פילאטיס ואימון פונקציונלי בשוהם"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#F5F0E8]/40 via-[#F5F0E8]/70 to-[#F5F0E8]/90" />
      </div>

      <div className="container relative z-10 pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          {/* Text content - right side in RTL */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="order-2 lg:order-1"
          >
            <div className="inline-block bg-[#2D5A3D]/10 rounded-full px-4 py-1.5 mb-6">
              <span className="text-[#2D5A3D] text-sm font-medium" style={{ fontFamily: "var(--font-body)" }}>
                30+ שנות ניסיון | בוגרת מכון וינגייט
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#1B4332] leading-tight mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              מאמנת כושר בשוהם
              <br />
              <span className="text-[#E8A87C]">להחזיר את הגוף לחיים</span>
            </h1>

            <p
              className="text-lg md:text-xl text-[#3D6B4F] leading-relaxed mb-8 max-w-lg"
              style={{ fontFamily: "var(--font-body)" }}
            >
              אימון אישי ובריאות תפקודית לכל גיל ומצב.
              <br />
              כי לגוף שלך מגיע לזוז, להרגיש טוב ולחיות באיכות.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="bg-[#E8A87C] hover:bg-[#d4956b] text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-[#E8A87C]/25 hover:-translate-y-0.5"
                style={{ fontFamily: "var(--font-body)" }}
              >
                ליצירת קשר ותיאום אימון
              </button>
              <button
                onClick={() => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" })}
                className="border-2 border-[#2D5A3D] text-[#2D5A3D] hover:bg-[#2D5A3D] hover:text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300"
                style={{ fontFamily: "var(--font-body)" }}
              >
                עוד עליי
              </button>
            </div>
          </motion.div>

          {/* Sharon's photo - left side in RTL */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="order-1 lg:order-2 flex justify-center"
          >
            <div className="relative">
              {/* Decorative blob behind photo */}
              <div className="absolute -inset-4 bg-[#2D5A3D]/10 rounded-[40%_60%_55%_45%/55%_45%_60%_40%] blur-sm" />
              <img
                src={SHARON_PILATES}
                alt="שרון גולדברג - מאמנת כושר בשוהם עם 30 שנות ניסיון"
                className="relative w-72 h-96 sm:w-80 sm:h-[28rem] lg:w-96 lg:h-[32rem] object-cover rounded-[30%_70%_60%_40%/50%_40%_60%_50%] shadow-2xl"
                loading="eager"
                fetchPriority="high"
                width={384}
                height={512}
              />
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-4 -right-4 bg-[#2D5A3D] text-white rounded-2xl px-5 py-3 shadow-xl"
              >
                <span className="text-2xl font-bold block" style={{ fontFamily: "var(--font-heading)" }}>30+</span>
                <span className="text-xs" style={{ fontFamily: "var(--font-body)" }}>שנות ניסיון</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown className="text-[#2D5A3D]/60" size={28} />
        </motion.div>
      </motion.div>

      {/* Organic wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z" fill="#F5F0E8" />
        </svg>
      </div>
    </section>
  );
}
